import { namedFieldTokenRegex } from '../documentTemplate';

import type { DocumentFlowDoc, FlowBlock, FlowInline } from './schema';

/**
 * A run of inline content with every well-formed `{{ token }}` turned into a field NODE.
 *
 * The model's guarantee is that a field is structure, not spelling — it cannot be half-deleted,
 * re-split, or typo'd into a name nobody linked. Import upholds that, but the editor did not: a
 * token typed by hand stayed text, which meant it was never offered for linking (the scan reads
 * the node tree) and was reported as "not a usable field name" even when the name was perfectly
 * good. Normalising restores the guarantee wherever a document enters or leaves the editor.
 *
 * Marks carry over to the field, so a bolded token stays bold.
 */
export const inlineWithFieldNodes = (content: FlowInline[]): FlowInline[] =>
	content.flatMap((node): FlowInline[] => {
		if (node.type !== 'text') {
			return [node];
		}
		const regex = namedFieldTokenRegex();
		const out: FlowInline[] = [];
		let cursor = 0;
		let match = regex.exec(node.text);
		while (match !== null) {
			const name = match[1];
			if (name !== undefined) {
				if (match.index > cursor) {
					out.push({
						type: 'text',
						text: node.text.slice(cursor, match.index),
						...(node.marks === undefined ? {} : { marks: node.marks }),
					});
				}
				const filter = match[2];
				out.push({
					type: 'field',
					attrs: { name, ...(filter === undefined ? {} : { filter }) },
					...(node.marks === undefined ? {} : { marks: node.marks }),
				});
				cursor = match.index + match[0].length;
			}
			match = regex.exec(node.text);
		}
		if (cursor === 0) {
			return [node];
		}
		if (cursor < node.text.length) {
			out.push({
				type: 'text',
				text: node.text.slice(cursor),
				...(node.marks === undefined ? {} : { marks: node.marks }),
			});
		}
		return out;
	});

const blocksWithFieldNodes = (blocks: FlowBlock[]): FlowBlock[] =>
	blocks.map((block): FlowBlock => {
		if (block.type === 'paragraph') {
			return block.content === undefined
				? block
				: { ...block, content: inlineWithFieldNodes(block.content) };
		}
		if (block.type === 'bulletList' || block.type === 'orderedList') {
			return {
				...block,
				content: block.content.map((item) => ({
					...item,
					content: blocksWithFieldNodes(item.content),
				})),
			};
		}
		if (block.type === 'table') {
			return {
				...block,
				content: block.content.map((row) => ({
					...row,
					content: row.content.map((cell) => ({
						...cell,
						content: blocksWithFieldNodes(cell.content),
					})),
				})),
			};
		}
		return block;
	});

/**
 * The document with every token written as text promoted to a field node.
 *
 * Applied where a document crosses a boundary — loaded into the editor, and saved back — rather
 * than on every keystroke, which would fight the caret.
 */
export const normaliseFlowFields = (doc: DocumentFlowDoc): DocumentFlowDoc => ({
	...doc,
	content: blocksWithFieldNodes(doc.content),
});
